import React from 'react';
import { computeCanonicalRender } from '../../lib/photoAligner';
import { apertureSize, explicitPhotoGeometry, transformString } from '../../lib/photoRender';

// The only thing allowed to draw a photo inside a share card.
//
// EXPORT SAFETY: no object-fit, no clip-path, no filter anywhere in here —
// html2canvas@1.4.1 implements none of them and fails silently. See
// lib/photoRender.js for the full explanation.
//
// The aperture is height-locked to 9:16 (the box the alignment was solved for),
// so the framing on the card is identical to the framing in the viewer. Excess
// width is clipped by the slot; missing width is filled by a pre-blurred
// backdrop from lib/blurBackdrop.js.
export default function AlignedPhotoFrame({
  url,
  backdropUrl = null,
  canonical,
  manual,
  natural,
  slotW,
  slotH,
  radius = 0,
  background = '#0a0a0a',
}) {
  const aperture = apertureSize(slotW, slotH);
  const { cxPct, cyPct, scale, dxPct, dyPct } = computeCanonicalRender(canonical);
  const geo = explicitPhotoGeometry({
    natural,
    boxW: aperture.width,
    boxH: aperture.height,
    cxPct,
    cyPct,
  });

  return (
    <div
      data-testid="aligned-slot"
      style={{
        position: 'relative',
        width: slotW,
        height: slotH,
        overflow: 'hidden',
        borderRadius: radius,
        background,
      }}
    >
      {/* Always rendered behind the aperture, not just when letterboxed: once
          the aperture is clipped (SplitCard portrait, EditorialCard portrait),
          manual.scale < 1 or a non-zero manual.translate — exactly what
          Auto-match produces — can pull the photo away from covering the
          slot, and without this the gap shows hard black instead of the same
          blurred backdrop the viewer would show. It is covered by the photo
          whenever the photo covers, so this is a no-op in the common case. */}
      {backdropUrl && (
        <img
          data-testid="aligned-backdrop"
          src={backdropUrl}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', left: 0, top: 0, width: slotW, height: slotH, opacity: 0.6 }}
        />
      )}

      {/* Centred with negative margins rather than translate(-50%,-50%) so the
          transform slot below stays free for the alignment transform — nested
          transforms are where html2canvas is least reliable. */}
      <div
        data-testid="aligned-aperture"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: aperture.width,
          height: aperture.height,
          marginLeft: -aperture.width / 2,
          marginTop: -aperture.height / 2,
          overflow: 'hidden',
        }}
      >
        {/* The transform box carries NO overflow of its own — see the clip
            wrapper immediately below for why. */}
        <div
          data-testid="aligned-transform-box"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: aperture.width,
            height: aperture.height,
            transform: `translate(${dxPct}%, ${dyPct}%) scale(${scale}) ${transformString(null, manual)}`,
            transformOrigin: `${cxPct}% ${cyPct}%`,
          }}
        >
          {/* THE CLIP MUST LIVE ON AN ELEMENT WITH NO TRANSFORM OF ITS OWN.
              html2canvas@1.4.1 silently ignores `overflow: hidden` on an
              element that also carries a `transform`. It was on the box above,
              which meant the browser clipped correctly in the preview while the
              EXPORT leaked the photo's uncropped edge — visible any time
              Auto-match scales a photo below panel-fill, i.e. routinely.
              Measured against a Canvas2D reference at manual.scale 0.8 + pan:
                overflow on the transformed box .... 1.628% of pixels wrong
                this untransformed wrapper ......... 0.000%
              The outer aperture clips fine for the same reason — it has no
              transform either. Do not merge these two divs back together. */}
          <div
            data-testid="aligned-clip"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: aperture.width,
              height: aperture.height,
              overflow: 'hidden',
            }}
          >
            <img
              data-testid="aligned-img"
              src={url}
              alt=""
              crossOrigin="anonymous"
              draggable={false}
              style={{ position: 'absolute', left: geo.left, top: geo.top, width: geo.width, height: geo.height }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
